import type { ReadingArticle } from "./reading-document";

type ArticlePages = Pick<ReadingArticle, "pageStart" | "pageEnd">;
type ResumeArticle = ArticlePages & Pick<ReadingArticle, "id">;

export type ReadingProgress = {
  pageNumber: number;
  progress: number;
};

export function readingResumeArticleId(
  articles: readonly ResumeArticle[],
  articleId?: string | null,
  savedPage?: number | null,
): string {
  if (articleId && (articleId === "issue-overview" || articles.some((article) => article.id === articleId))) {
    return articleId;
  }
  if (savedPage == null || !Number.isFinite(savedPage) || savedPage <= 1) {
    return "issue-overview";
  }
  const ordered = articles
    .filter((article) => Number.isFinite(article.pageStart)
      && Number.isFinite(article.pageEnd)
      && article.pageStart >= 1
      && article.pageEnd >= article.pageStart)
    .sort((left, right) => left.pageStart - right.pageStart);
  const page = Math.floor(savedPage);
  const containing = ordered.find((article) => article.pageStart <= page && article.pageEnd >= page);
  const next = ordered.find((article) => article.pageStart > page);
  return containing?.id ?? next?.id ?? ordered.at(-1)?.id ?? "issue-overview";
}

/** Converts a 0–1 scroll ratio within an article to the existing PDF progress format. */
export function articleReadingProgress(
  article: ArticlePages,
  articleProgress: number,
  pageCount: number,
): ReadingProgress {
  const total = Number.isFinite(pageCount) ? Math.max(1, Math.floor(pageCount)) : 1;
  const start = Number.isFinite(article.pageStart)
    ? Math.max(1, Math.min(total, Math.floor(article.pageStart)))
    : 1;
  const end = Number.isFinite(article.pageEnd)
    ? Math.max(start, Math.min(total, Math.floor(article.pageEnd)))
    : start;
  const ratio = Number.isFinite(articleProgress) ? Math.max(0, Math.min(1, articleProgress)) : 0;
  const pageNumber = Math.min(end, start + Math.floor(ratio * (end - start + 1)));
  return { pageNumber, progress: pageNumber / total };
}
